// Registro unico de timers/rafs do modulo.
// Objetivo: facilitar cleanup no onUnmounted com `releaseAll()`.

export interface AsyncRegistry<
	TimerKeys extends string,
	RafKeys extends string
> {
	timers: Record<TimerKeys, ReturnType<typeof setTimeout> | null>;
	rafs: Record<RafKeys, number | null>;
	clearTimer(key: TimerKeys): void;
	cancelRaf(key: RafKeys): void;
	releaseAll(): void;
}

export function createAsyncRegistry<
	TimerKeys extends string,
	RafKeys extends string
>(
	timerKeys: readonly TimerKeys[],
	rafKeys: readonly RafKeys[]
): AsyncRegistry<TimerKeys, RafKeys> {
	const timers = Object.fromEntries(timerKeys.map(k => [k, null])) as Record<
		TimerKeys,
		ReturnType<typeof setTimeout> | null
	>;

	const rafs = Object.fromEntries(rafKeys.map(k => [k, null])) as Record<
		RafKeys,
		number | null
	>;

	// Limpa um timer especifico e zera a referencia.
	function clearTimer(key: TimerKeys): void {
		const id = timers[key];
		if (id !== null) {
			clearTimeout(id);
			timers[key] = null;
		}
	}

	// Cancela um RAF especifico e zera a referencia.
	function cancelRaf(key: RafKeys): void {
		const id = rafs[key];
		if (id !== null) {
			cancelAnimationFrame(id);
			rafs[key] = null;
		}
	}

	// Limpa todos os recursos registrados.
	function releaseAll(): void {
		timerKeys.forEach(clearTimer);
		rafKeys.forEach(cancelRaf);
	}

	return { timers, rafs, clearTimer, cancelRaf, releaseAll };
}
